import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage, visit } from "@hotwired/turbo"
import { CARD_SELECTOR, Queue, SEND_DELAY, UNITS_SELECTOR, acceptsTap, barFromStream, bars, confirmedByBar, outranked, paintedUnits, readCard, readCards, shown, writeCards, writeUnits } from "../modules/card_action.mjs"
import { rememberBar } from "../modules/native_cart_resume"

// "+" y "−" de las tarjetas: pinta en el toque, agrupa los toques por producto y manda la cantidad objetivo, un
// request a la vez; las tarjetas de un frame entran por adopt y un morph (resultados del buscador) no toca las
// ocupadas. Doc: storefront_quick_add.md → Decisiones (toques agrupados) y storefront_catalog.md
export default class extends Controller {
  connect() {
    this.queue = new Queue()
    this.timers = new Map() // debounce por producto
    this.lastTap = new Map() // throttle por producto
    this.forms = new Map() // la tarjeta viva de cada producto tocado (action, token, product_id)
    this.incoming = [] // tarjetas del frame que viene, hasta que Turbo las inserta
    this.leaving = false // la página se va (before-cache o desconectada): lo que salga va con keepalive
    this.onBeforeCache = () => { this.leaving = true; this.#forget() }
    this.onBeforeFrameRender = event => { // la sección se va: lo pendiente sale congelado; lo que viene, adoptado
      this.#flush({ frozen: true })
      this.incoming = Array.from(event.detail.newFrame?.querySelectorAll(CARD_SELECTOR) ?? [])
      this.#adopt(this.incoming)
    }
    this.onFrameRender = () => { // insertadas: una barra que llegó mientras tanto las pone al día, menos las ocupadas
      this.incoming = []
      this.forms.forEach((form, id) => { if (!form.isConnected && !this.#busy(id)) this.forms.delete(id) })
      this.#reconcile()
    }
    // Resultados morfeados (buscador). Una tarjeta ocupada que sigue siendo su producto no se toca: muestra su
    // intención y la barra la pondrá al día. Si su producto sale, el nodo pasa a ser de otro o se quita un
    // contenedor con tarjetas ocupadas adentro, lo pendiente sale fijado (#leave)
    this.onBeforeMorphElement = event => {
      const { newElement } = event.detail
      const target = event.target
      if (target.matches?.(CARD_SELECTOR)) {
        if (!this.#busy(target.dataset.productId)) return
        if (newElement?.dataset?.productId === target.dataset.productId) event.preventDefault()
        else this.#leave(target)
      } else if (!newElement) {
        this.#busyFormsIn(target).forEach(form => this.#leave(form))
      }
    }
    // Un stream que actualiza algo de esta vitrina (resultados): después de aplicarlo, la barra pintada manda
    // sobre las tarjetas libres, morfeadas o recién insertadas (una insertada no recibe eventos por tarjeta)
    this.onBeforeStreamRender = event => {
      const container = document.getElementById(event.target.target ?? "")
      if (!container || !this.element.contains(container)) return
      const render = event.detail.render
      event.detail.render = async stream => {
        await render(stream)
        this.#reconcile()
      }
    }
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    this.element.addEventListener("turbo:before-frame-render", this.onBeforeFrameRender)
    this.element.addEventListener("turbo:frame-render", this.onFrameRender)
    this.element.addEventListener("turbo:before-morph-element", this.onBeforeMorphElement)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    this.element.removeEventListener("turbo:before-frame-render", this.onBeforeFrameRender)
    this.element.removeEventListener("turbo:frame-render", this.onFrameRender)
    this.element.removeEventListener("turbo:before-morph-element", this.onBeforeMorphElement)
    this.leaving = true
    this.#flush({ frozen: true }) // lo que esperaba el debounce sale igual
  }

  enqueue(event) {
    event.preventDefault()
    const form = event.currentTarget
    const id = form.dataset.productId
    const delta = Number(event.submitter?.value) || 1
    const now = performance.now()
    if (!acceptsTap(this.lastTap.get(id), now)) return
    if (!this.#apply(cards => this.queue.tap(cards, id, delta))) return
    this.lastTap.set(id, now)
    this.forms.set(id, this.incoming.find(other => other.dataset.productId === id && !other.isConnected) ?? form)
    this.#markBusy(id) // intención pendiente: la barra no toca esta tarjeta
    clearTimeout(this.timers.get(id))
    this.timers.set(id, setTimeout(() => {
      this.timers.delete(id)
      // Si su sección ya salió, el objetivo se fija desde lo que la tarjeta mostraba
      this.queue.queue(id, { form, frozen: !form.isConnected, cards: { [id]: readCard(form) } })
      this.#drain()
    }, SEND_DELAY))
  }

  // Lo que espera el debounce sale ya; `frozen` fija la cantidad desde la propia tarjeta (viva o ya fuera de la página)
  #flush({ frozen = false } = {}) {
    for (const [id, timer] of this.timers) {
      clearTimeout(timer)
      const form = this.forms.get(id)
      this.queue.queue(id, { form, frozen, cards: form ? { [id]: readCard(form) } : null })
    }
    this.timers.clear()
    if (frozen) this.queue.ops.forEach(op => { op.frozenTarget ??= shown(op.form ? readCard(op.form) : { confirmed: 0, pending: 0 }) })
    this.#drain()
  }

  async #drain() {
    if (this.sending) return
    this.sending = true
    let op
    while ((op = this.queue.take(readCards(this.element.querySelectorAll(CARD_SELECTOR))))) await this.#send(op)
    this.sending = false
    this.#settleAll() // también las que take descartó
  }

  async #send(op) {
    const form = op.form ?? this.forms.get(op.id) // action, token y product_id: sirve aunque su tarjeta ya no esté
    this.#markBusy(op.id)
    try {
      const body = new FormData(form)
      body.set("quantity", String(op.target))
      // Desde una página que se va, keepalive: el navegador lo termina aunque cambie la página
      const response = await fetch(form.action, { method: "POST", body, keepalive: this.leaving, headers: { Accept: "text/vnd.turbo-stream.html" } })
      if (response.headers.get("Content-Type")?.includes("turbo-stream")) {
        const text = await response.text()
        if (response.ok) this.#confirm(text)
        else this.#reject(text)
      } else if (response.redirected) {
        visit(response.url) // sesión vencida: el login
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      console.error("No se pudo actualizar el carrito", error)
      if (!this.element.isConnected) {
        // La página ya quedó atrás y el WebView cortó el request: lo que siga en cola sale igual (keepalive) y no
        // se avisa; la página nueva y la barra traen el dato. Nada que pintar
        this.queue.dropInflight()
      } else {
        this.#dropTimers() // un debounce vivo mandaría la cantidad "restaurada"
        this.#apply(cards => this.queue.failed(cards, paintedUnits()))
        this.#allForms().forEach(other => other.removeAttribute("aria-busy"))
        window.toastr?.error("No se pudo actualizar tu carrito. Intenta de nuevo.")
      }
    } finally {
      this.#settle(op.id)
    }
  }

  // 200: confirma la operación en vuelo; superada si su versión es menor que la última barra de la cuenta
  #confirm(text) {
    const bar = barFromStream(text)
    const id = this.queue.busyId
    const older = bar === null ? null : outranked(bar, bars.latestFor(this.#accountId()), id)
    const clamped = older === null && /data-controller="flash"/.test(text)
    const units = older ? older.units : (bar?.units ?? null)
    this.#apply(cards => this.queue.ok(cards, units, { replay: older !== null, clamped, superseded: older?.changed ?? false }))
    if (bar && older === null) rememberBar(bar.container, this.#accountId()) // etapa E
    renderStreamMessage(text)
    this.#followUp(id)
  }

  // 422: descarta la intención del producto; el toast solo si la página sigue
  #reject(text) {
    const id = this.queue.busyId
    clearTimeout(this.timers.get(id))
    this.timers.delete(id)
    this.#apply(cards => this.queue.rejected(cards, { units: paintedUnits() }))
    if (this.element.isConnected) renderStreamMessage(text)
  }

  #dropTimers() {
    this.timers.forEach(timer => clearTimeout(timer))
    this.timers.clear()
  }

  // Lo tocado durante el vuelo pide un request más; nunca desde una página o una tarjeta que ya no están
  #followUp(id) {
    const form = this.forms.get(id)
    if (!this.element.isConnected || !form?.isConnected || this.timers.has(id) || this.queue.isQueued(id)) return
    const card = readCard(form)
    if (shown(card) !== card.confirmed) this.queue.queue(id, { form })
  }

  // Un producto con intención que sale de los resultados (o cuya tarjeta pasa a ser de otro producto): lo que
  // esperaba en el debounce o en la cola sale fijado desde lo que mostraba, con una copia del form porque el morph
  // muta el nodo en el acto; lo que ya está en vuelo sigue su curso y no se repite
  #leave(form) {
    const id = form.dataset.productId
    const pending = this.timers.has(id) || this.queue.isQueued(id)
    const copy = form.cloneNode(true)
    this.forms.set(id, copy)
    clearTimeout(this.timers.get(id))
    this.timers.delete(id)
    if (pending) this.queue.queue(id, { form: copy, frozen: true, cards: { [id]: readCard(form) } })
    this.#drain()
  }

  // Tras un stream de la vitrina: la barra pintada (lo último del servidor) confirma las tarjetas libres; una tarjeta
  // ocupada recién insertada (su producto volvió con el "+" en vuelo o encolado fijado) no trae su intención: la
  // recupera como las de un frame (adopt) y pasa a ser la tarjeta viva de ese producto
  #reconcile() {
    const units = paintedUnits()
    if (units) this.#apply(cards => confirmedByBar(cards, units, Object.keys(cards).filter(id => this.#busy(id))))
    if (units) writeUnits(this.element.querySelectorAll(UNITS_SELECTOR), units) // las tarjetas con opciones recién insertadas
    const inserted = this.#allForms().filter(form => this.#busy(form.dataset.productId) && this.forms.get(form.dataset.productId) !== form)
    if (inserted.length) this.#adopt(inserted)
  }

  #busyFormsIn(node) {
    return Array.from(node.querySelectorAll?.(CARD_SELECTOR) ?? []).filter(form => this.#busy(form.dataset.productId))
  }

  // Ocupado mientras tenga debounce, cola o vuelo
  #busy(id) {
    return this.timers.has(id) || this.queue.isQueued(id) || this.queue.busyId === id
  }

  #markBusy(id) {
    this.#formsOf(id).forEach(form => form.setAttribute("aria-busy", "true"))
  }

  #settle(id) {
    if (!this.#busy(id)) this.#formsOf(id).forEach(form => form.removeAttribute("aria-busy"))
  }

  #settleAll() {
    this.forms.forEach((form, id) => this.#settle(id))
  }

  // Tarjetas de un frame, con la cola recién congelada: la barra pintada confirma; un producto con intención
  // queda ocupado mostrando su objetivo
  #adopt(forms) {
    writeCards(forms, this.queue.adopt(readCards(forms), paintedUnits()))
    for (const form of forms) {
      const id = form.dataset.productId
      if (!this.#busy(id)) { form.removeAttribute("aria-busy"); continue }
      form.setAttribute("aria-busy", "true")
      this.forms.set(id, form)
    }
  }

  // Las tarjetas de ESTA página y las que vienen en un frame (hasta que se insertan)
  #allForms() {
    return [...this.element.querySelectorAll(CARD_SELECTOR), ...this.incoming.filter(form => !form.isConnected)]
  }

  #formsOf(id) {
    return this.#allForms().filter(form => form.dataset.productId === id)
  }

  // Lee las tarjetas, aplica un paso y pinta lo que cambió
  #apply(step) {
    const forms = this.#allForms()
    const next = step(readCards(forms))
    if (next) writeCards(forms, next)
    return next
  }

  #accountId() {
    return (this.element.querySelector(".account-data") ?? document.querySelector(".account-data"))?.dataset.accountId
  }

  // turbo:before-cache: lo pendiente sale con cantidad fijada; el snapshot queda sin intenciones
  #forget() {
    this.#flush({ frozen: true })
    this.#apply(cards => this.queue.reset(cards))
    this.#allForms().forEach(form => form.removeAttribute("aria-busy"))
  }
}
